<template>
    <BaseDialog v-model:open="isOpen" title="Your goals for Workday">
        <div v-if="draftError" class="flex flex-col items-center gap-3 py-6 text-center" data-testid="goal-draft-error">
            <p class="body-small text-notification-error-text" role="alert">{{ draftError }}</p>
            <BaseButton theme="primary" @click="isOpen = false">Close</BaseButton>
        </div>
        <div v-else-if="!draft" class="flex flex-col items-center gap-3 py-10 text-center" data-testid="goal-draft-loading">
            <LoadingSpinner class="h-6" />
            <p class="nadia-chat text-ink-muted">Drafting your goals from the conversation…</p>
        </div>
        <div v-else class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <p class="body-small text-ink-muted">{{ subtitle }}</p>
            </div>

            <template v-if="!submitted">
                <section v-for="(goal, index) in localGoals" :key="index" class="flex flex-col gap-2 pl-3 border-l-2 border-border">
                    <div class="flex items-center justify-between">
                        <h4 class="body-semibold text-ink">Goal {{ index + 1 }}</h4>
                        <BaseButton theme="tertiary" :disabled="loading" :aria-label="`Remove goal ${index + 1}`" @click="removeGoal(index)">Remove</BaseButton>
                    </div>
                    <BaseTextarea
                        v-model:input="goal.name"
                        rows="1"
                        :disabled="loading"
                        aria-label="Goal title"
                        placeholder="Goal title"
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    <BaseTextarea
                        v-model:input="goal.description"
                        rows="2"
                        :disabled="loading"
                        aria-label="Goal description"
                        placeholder="Describe the goal and what success looks like."
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    <label :for="`due-${index}`" class="goal-field">
                        <span class="goal-field-label">Due date (optional)</span>
                        <input :id="`due-${index}`" v-model="goal.due_date" type="date" :disabled="loading" class="goal-select" />
                    </label>
                    <label v-if="categoryOptions.length" :for="`cat-${index}`" class="goal-field">
                        <span class="goal-field-label">Goal type</span>
                        <select :id="`cat-${index}`" v-model="goal.goal_category_id" :disabled="loading" class="goal-select">
                            <option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                        </select>
                    </label>
                    <label v-if="orgGoalOptions.length" :for="`org-goal-${index}`" class="goal-field">
                        <span class="goal-field-label">Aligns to organization goal (optional)</span>
                        <BaseCombobox
                            v-model="goal.organizationGoalOption"
                            :options="orgGoalOptions"
                            :input-id="`org-goal-${index}`"
                            :disabled="loading"
                            placeholder="Search organization goals…" />
                    </label>
                </section>

                <div>
                    <BaseButton theme="tertiary" :disabled="loading" data-testid="add-goal" @click="addGoal">+ Add another goal</BaseButton>
                </div>

                <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="goal-push-submitted">
                <i class="bi bi-check-circle text-2xl text-chip-status-done-text" aria-hidden="true"></i>
                <p class="nadia-chat text-ink">{{ submittedMessage }}</p>
                <BaseButton theme="primary" @click="isOpen = false">Done</BaseButton>
            </div>
        </div>
        <template v-if="draft && !draftError && !submitted" #footer>
            <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">Cancel</BaseButton>
            <BaseButton
                theme="primary"
                data-testid="submit-goal-push-dialog"
                :disabled="loading || !hasContent"
                class="flex items-center justify-center gap-1"
                @click="handleSubmit">
                <LoadingSpinner v-if="loading" class="h-4" />{{ editingExisting ? "Update in Workday" : "Push to Workday" }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref, watch } from "vue";

interface DraftGoal {
    goal_id: string;
    name: string;
    description: string;
    due_date: string;
    goal_category_id: string;
    organization_goal_id: string;
}
interface GoalPushDraft {
    goals: DraftGoal[];
}
interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    draft: GoalPushDraft | null;
    messageId: number;
    // The draft was reloaded from goals already in Workday (editing existing), not a fresh draft.
    editingExisting?: boolean;
    // Per-tenant options for the category and org-goal alignment selectors; empty hides the control.
    categoryOptions?: SelectOption[];
    orgGoalOptions?: SelectOption[];
    // Set when the background draft fetch failed; shown in place of the goal list.
    draftError?: string;
}

const props = withDefaults(defineProps<Props>(), {
    editingExisting: false,
    categoryOptions: () => [],
    orgGoalOptions: () => [],
    draftError: "",
});
const isOpen = defineModel<boolean>("open", { default: false });

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
const submittedMessage = ref("");
const noticeMessage = ref("");

const subtitle = computed(() =>
    props.editingExisting
        ? "These are the goals you already pushed to Workday. Edit them (changes update the same goals) or add new ones."
        : "Drafted from your conversation. Edit, remove, or add a goal before pushing to Workday.",
);

// Client-side row: the org-goal alignment is held as the selected combobox option (resolved back
// to organization_goal_id on submit), the rest mirrors the server DraftGoal shape.
interface LocalGoal {
    goal_id: string;
    name: string;
    description: string;
    due_date: string;
    goal_category_id: string;
    organizationGoalOption: SelectOption | null;
}

function toLocalGoals(source: GoalPushDraft | null): LocalGoal[] {
    if (!source) {
        return [];
    }
    return source.goals.map((goal) => ({
        goal_id: goal.goal_id,
        name: goal.name,
        description: goal.description,
        due_date: goal.due_date,
        goal_category_id: goal.goal_category_id,
        organizationGoalOption: props.orgGoalOptions.find((option) => option.value === goal.organization_goal_id) ?? null,
    }));
}

const localGoals = reactive<LocalGoal[]>(toLocalGoals(props.draft));

const hasContent = computed(() => localGoals.some((goal) => goal.name.trim().length > 0));

// Reset the editable copy whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            localGoals.splice(0, localGoals.length, ...toLocalGoals(draft));
            submitted.value = false;
            submittedMessage.value = "";
            noticeMessage.value = "";
        }
    },
    { immediate: true },
);

function addGoal() {
    localGoals.push({ goal_id: "", name: "", description: "", due_date: "", goal_category_id: "", organizationGoalOption: null });
}

function removeGoal(index: number) {
    localGoals.splice(index, 1);
}

function pushSummary(created: number, updated: number): string {
    const parts: string[] = [];
    if (created > 0) {
        parts.push(created === 1 ? "pushed 1 new goal" : `pushed ${created} new goals`);
    }
    if (updated > 0) {
        parts.push(updated === 1 ? "updated 1 goal" : `updated ${updated} goals`);
    }
    const body = parts.length > 0 ? parts.join(" and ") : "pushed your goals";
    return `${body.charAt(0).toUpperCase()}${body.slice(1)} in Workday.`;
}

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_goals_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const goals = localGoals
            .map((goal) => ({
                goal_id: goal.goal_id,
                name: goal.name.trim(),
                description: goal.description.trim(),
                due_date: goal.due_date.trim(),
                goal_category_id: goal.goal_category_id,
                organization_goal_id: goal.organizationGoalOption?.value ?? "",
            }))
            .filter((goal) => goal.name.length > 0);
        const result = await execute(ChatAction.SUBMIT_GOAL_PUSH_DRAFT, { message_id: props.messageId, draft: { goals } }, { sync: true });
        if (result?.success) {
            submittedMessage.value = pushSummary(result.payload?.created_count ?? goals.length, result.payload?.updated_count ?? 0);
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't push your goals to Workday. Please try again later.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't push your goals to Workday. Please try again later.";
    } finally {
        stopLoading();
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .goal-field {
        @apply flex flex-col gap-1;
    }

    .goal-field-label {
        @apply body-small text-control-label;
    }

    .goal-select {
        @apply rounded-lgr border border-control-border bg-control-bg px-3 py-2 body-small text-control-text-strong disabled:opacity-50;
    }
}
</style>
